/**
 * AI Activity page — paginated log of AI provider events with live SSE refresh.
 *
 * Route: /ai-activity
 * Clears the sidebar unread badge on mount.
 *
 * Server state is managed by React Query (see src/queries/ai-events/keys.ts).
 * Live SSE updates invalidate the query via queryClient.invalidateQueries so
 * React Query handles the refetch rather than a manual useEffect + setState.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, ChevronRight, Info, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { fetchAIEvents, AIEventRecord, FetchAIEventsParams } from '../api/aiEventsApi.ts';
import { useAIEvents } from '../hooks/useAIEvents.ts';
import { aiEventsKeys } from '../queries/ai-events/keys.ts';

// ── helpers ────────────────────────────────────────────────────────────────

function severityBadge(severity: string) {
  switch (severity) {
    case 'error':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <AlertTriangle className="h-3 w-3" /> Error
        </span>
      );
    case 'warning':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
          <AlertTriangle className="h-3 w-3" /> Warning
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
          <Info className="h-3 w-3" /> Info
        </span>
      );
  }
}

function providerLabel(provider: string) {
  const map: Record<string, string> = {
    anthropic: 'Anthropic',
    gemini: 'Gemini',
    llama_cloud: 'LlamaCloud',
    openai: 'OpenAI',
    groq: 'Groq',
  };
  return map[provider] ?? provider;
}

function eventTypeLabel(type: string) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Maps api_endpoint paths to human-readable feature names shown in Source column.
const SOURCE_LABELS: Record<string, string> = {
  '/api/engagement/chat-stream':              'Engagement Chat',
  '/api/engagement/analyze-stream':           'Engagement Analysis',
  '/api/evidence/analyze-stream':             'Evidence Analysis',
  '/api/attribute-test/extract-evidence':     'Evidence Extraction',
  '/api/attribute-test/execute-stream':       'Attribute Test',
  '/api/test-execution/stream':               'Test Execution',
  '/api/controls/generate-test-steps-stream': 'Test Step Generation',
  '/api/evidence/annotate':                   'Evidence Annotation',
};

function sourceLabel(endpoint?: string | null): string {
  if (!endpoint) return '—';
  return SOURCE_LABELS[endpoint] ?? endpoint.replace(/^\/api\//, '');
}

// ── filter bar ─────────────────────────────────────────────────────────────

const PROVIDERS = ['', 'anthropic', 'gemini', 'llama_cloud', 'openai', 'groq'];
const SEVERITIES = ['', 'error', 'warning', 'info'];
const HOURS_OPTIONS = [
  { label: 'Last 24 h', value: 24 },
  { label: 'Last 7 days', value: 168 },
];

interface Filters {
  provider: string;
  severity: string;
  hours: number;
}

// ── page ───────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export default function AIActivity() {
  const queryClient = useQueryClient();

  // Single context subscription — all three values destructured here.
  const { clearUnread, connected, latestEvent } = useAIEvents();

  // UI state only — server state is owned by React Query below.
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [filters, setFilters] = useState<Filters>({
    provider: '',
    severity: '',
    hours: 24,
  });

  // Clear sidebar badge when this page mounts.
  useEffect(() => {
    clearUnread();
  }, [clearUnread]);

  // Build params object that drives both the query key and the fetch call.
  const queryParams: FetchAIEventsParams = {
    provider: filters.provider || undefined,
    severity: filters.severity || undefined,
    hours: filters.hours,
    page,
    page_size: PAGE_SIZE,
  };

  const {
    data,
    isLoading,   // true only on the very first fetch (no cached data yet)
    isFetching,  // true on every in-progress fetch (used for spinner)
    isError,
    refetch,
  } = useQuery({
    queryKey: aiEventsKeys.list(queryParams),
    queryFn: () => fetchAIEvents(queryParams),
  });

  const events: AIEventRecord[] = data?.events ?? [];
  const total: number = data?.total ?? 0;

  // When a new SSE event arrives, jump to page 1 and invalidate so React Query
  // refetches. invalidateQueries covers all ai-events keys (every page / filter
  // combo) so stale data elsewhere is cleared too. Because setPage(1) is batched
  // with the next render, the invalidation targets the page-1 key the component
  // will settle on — no double-fetch.
  //
  // isMountRef skips the effect on the initial render: the context may already
  // hold events seeded from its 24-hour API call, so without this guard a
  // redundant invalidateQueries fires immediately on page open.
  const isMountRef = useRef(true);
  const latestId = latestEvent?.id;
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    if (latestId) {
      setPage(1);
      queryClient.invalidateQueries({ queryKey: aiEventsKeys.all });
    }
  }, [latestId, queryClient]);

  const handleFilterChange = (key: keyof Filters, value: string | number) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">AI Activity</h1>
          <p className="mt-1 text-sm text-gray-500">
            Real-time log of AI provider errors and warnings across your organisation.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {connected ? (
            <span className="flex items-center gap-1 text-green-600">
              <Wifi className="h-4 w-4" />
              Live
            </span>
          ) : (
            <span className="flex items-center gap-1 text-gray-400">
              <WifiOff className="h-4 w-4" />
              Reconnecting…
            </span>
          )}
          <button
            onClick={() => refetch()}
            className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Provider */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Provider</label>
          <select
            value={filters.provider}
            onChange={(e) => handleFilterChange('provider', e.target.value)}
            className="text-sm rounded-md border border-gray-300 px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All providers</option>
            {PROVIDERS.filter(Boolean).map((p) => (
              <option key={p} value={p}>{providerLabel(p)}</option>
            ))}
          </select>
        </div>

        {/* Severity */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Severity</label>
          <select
            value={filters.severity}
            onChange={(e) => handleFilterChange('severity', e.target.value)}
            className="text-sm rounded-md border border-gray-300 px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All severities</option>
            {SEVERITIES.filter(Boolean).map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Time window */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Window</label>
          <select
            value={filters.hours}
            onChange={(e) => handleFilterChange('hours', Number(e.target.value))}
            className="text-sm rounded-md border border-gray-300 px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {HOURS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <span className="ml-auto text-sm text-gray-400 tabular-nums">
          {total.toLocaleString()} event{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Error banner */}
      {isError && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          Failed to load AI events. Please try again.
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 w-40">Time</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 w-28">Provider</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 w-28">Severity</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 w-36">Event Type</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 w-44">Source</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Message</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600 w-16">HTTP</th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-400" />
                  No AI events in this time window — all providers healthy.
                </td>
              </tr>
            ) : (
              events.map((ev) => {
                const isExpanded = expandedId === ev.id;
                const meta = ev.metadata as Record<string, string> | undefined;
                const agent = meta?.agent;
                const requestId = meta?.request_id;
                const model = meta?.model;
                const providerMessage = meta?.provider_message;
                return (
                  <React.Fragment key={ev.id}>
                    <tr
                      className={`transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                    >
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap tabular-nums">
                        {formatDate(ev.created_at)}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                        {providerLabel(ev.provider)}
                      </td>
                      <td className="px-4 py-3">{severityBadge(ev.severity)}</td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {eventTypeLabel(ev.event_type)}
                      </td>
                      <td className="px-4 py-3">
                        {ev.api_endpoint ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap">
                            {sourceLabel(ev.api_endpoint)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs">
                        <span className="line-clamp-2">{ev.error_message || '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 tabular-nums">
                        {ev.http_status ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        <ChevronRight
                          className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? 'rotate-90 text-blue-500' : ''}`}
                        />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-blue-50 border-t border-blue-100">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Endpoint</p>
                              <p className="font-mono text-gray-800 break-all">{ev.api_endpoint || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Agent / Module</p>
                              <p className="font-mono text-gray-800">{agent || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Full message</p>
                              <p className="text-gray-800">{ev.error_message || '—'}</p>
                            </div>
                            {model && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Model</p>
                                <p className="font-mono text-gray-800">{model}</p>
                              </div>
                            )}
                            {providerMessage && (
                              <div className="col-span-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Provider response</p>
                                <p className="font-mono text-gray-600 text-xs break-all">{providerMessage}</p>
                              </div>
                            )}
                            {requestId && (
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Provider Request ID</p>
                                <p className="font-mono text-gray-600 text-xs break-all">{requestId}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-gray-500 tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
